import type { SVGProps } from "react";

export type IconProps = Omit<SVGProps<SVGSVGElement>, "children">;

// 24px stroke icons. They carry no size or stroke styling of their own: callers pass classes (or style them from a
// parent with `[&_svg]:` variants, as the buttons and rail links do). Always decorative, so hidden from assistive tech.
function Svg(props: SVGProps<SVGSVGElement>) {
	return <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false" {...props} />;
}

/**
 * The Ravnar bird from ravnar.io (the same path as its ravnar-mark.svg and favicon). Unlike the stroke icons it is a
 * filled glyph on its own 550×506 grid: it paints with currentColor, so callers set only its size and text color.
 */
export function LogoIcon(props: IconProps) {
	return (
		<svg viewBox="0 0 550 506" fill="currentColor" aria-hidden="true" focusable="false" {...props}>
			<path
				transform="translate(0,506) scale(0.1,-0.1)"
				d="M256 4771 c-9 -56 -7 -182 5 -288 39 -351 199 -610 499 -808 155 -102 377 -230 1060 -612 162 -91 297 -167 299 -169 20 -18 -30 -4 -174 52 -94 35 -249 94 -345 131 -209 79 -722 281 -835 328 -44 18 -146 61 -228 96 -81 34 -150 59 -153 56 -11 -11 16 -168 42 -247 112 -339 323 -555 670 -686 118 -44 637 -197 1069 -315 128 -35 131 -36 123 -45 -7 -6 -350 31 -748 82 -388 49 -703 85 -708 80 -17 -18 80 -223 150 -318 124 -169 344 -305 574 -355 91 -20 432 -24 707 -9 87 4 157 4 157 0 0 -5 -24 -22 -54 -37 -148 -78 -269 -203 -621 -642 -115 -143 -275 -341 -355 -440 -238 -292 -300 -370 -300 -377 0 -5 143 -8 318 -8 l318 0 115 128 c279 309 743 841 1236 1416 173 203 244 348 259 536 16 189 -72 401 -225 542 -35 33 -518 361 -1158 787 -1451 966 -1451 965 -1579 1069 l-112 89 -6 -36z M3595 3506 c-112 -21 -278 -82 -378 -136 -101 -56 -286 -172 -286 -180 -1 -3 47 -39 105 -80 272 -191 415 -425 431 -705 10 -169 -40 -348 -139 -501 -51 -79 -425 -531 -574 -694 l-24 -26 242 -269 c132 -149 288 -323 347 -388 153 -171 249 -238 393 -272 68 -16 809 -23 807 -7 0 4 -31 31 -70 61 -38 30 -93 75 -122 100 -30 25 -109 93 -176 150 -67 57 -129 114 -139 126 -17 23 -310 358 -529 607 -68 76 -123 142 -123 146 0 4 36 26 78 50 289 159 487 397 568 683 53 185 151 327 296 427 30 20 61 57 89 102 45 73 63 88 54 45 -3 -14 -7 -36 -10 -50 -3 -14 -4 -25 -2 -25 2 0 48 20 101 45 54 25 133 55 177 67 43 12 79 24 79 28 0 4 -33 11 -72 14 -113 11 -148 18 -148 29 0 20 596 -4 671 -28 37 -12 19 16 -59 93 -128 127 -279 203 -494 248 -45 9 -112 17 -148 18 l-66 1 -41 57 c-80 109 -214 193 -388 244 -111 32 -336 42 -450 20z m533 -348 c160 -21 189 -30 135 -41 -16 -3 -52 -24 -82 -46 -106 -79 -209 -64 -262 40 -10 22 -28 45 -39 51 -11 7 -20 17 -20 21 0 10 23 7 268 -25z"
			/>
		</svg>
	);
}

export function BriefcaseIcon(props: IconProps) {
	return (
		<Svg {...props}>
			<rect x="3" y="7" width="18" height="13" rx="2" />
			<path d="M8 7V5a2 2 0 012-2h4a2 2 0 012 2v2" />
		</Svg>
	);
}

export function PersonIcon(props: IconProps) {
	return (
		<Svg {...props}>
			<circle cx="12" cy="8" r="3.5" />
			<path d="M5 20c0-3.3 3.1-6 7-6s7 2.7 7 6" />
		</Svg>
	);
}

export function BellIcon(props: IconProps) {
	return (
		<Svg {...props}>
			<path d="M6 9a6 6 0 1112 0c0 5 2 6 2 6H4s2-1 2-6" />
			<path d="M10 20a2 2 0 004 0" />
		</Svg>
	);
}

export function SunIcon(props: IconProps) {
	return (
		<Svg {...props}>
			<circle cx="12" cy="12" r="4" />
			<path d="M12 2v2M12 20v2M2 12h2M20 12h2M5 5l1.5 1.5M17.5 17.5L19 19M19 5l-1.5 1.5M6.5 17.5L5 19" />
		</Svg>
	);
}

export function SignOutIcon(props: IconProps) {
	return (
		<Svg {...props}>
			<path d="M15 4h3a2 2 0 012 2v12a2 2 0 01-2 2h-3M10 17l5-5-5-5M15 12H3" />
		</Svg>
	);
}

export function SearchIcon(props: IconProps) {
	return (
		<Svg {...props}>
			<circle cx="11" cy="11" r="7" />
			<path d="M20 20l-3.5-3.5" />
		</Svg>
	);
}

export function PlusIcon(props: IconProps) {
	return (
		<Svg {...props}>
			<path d="M12 5v14M5 12h14" />
		</Svg>
	);
}

export function SettingsIcon(props: IconProps) {
	return (
		<Svg {...props}>
			<circle cx="12" cy="12" r="3" />
			<path d="M12 3v2M12 19v2M3 12h2M19 12h2" />
		</Svg>
	);
}

export function HomeIcon(props: IconProps) {
	return (
		<Svg {...props}>
			<path d="M4 11l8-7 8 7v8a1 1 0 01-1 1H5a1 1 0 01-1-1z" />
		</Svg>
	);
}

export function TrayIcon(props: IconProps) {
	return (
		<Svg {...props}>
			<path d="M4 14h4l1.5 2.5h5L16 14h4" />
			<path d="M6.5 6h11L20 14v4a2 2 0 01-2 2H6a2 2 0 01-2-2v-4z" />
		</Svg>
	);
}

export function UploadIcon(props: IconProps) {
	return (
		<Svg {...props}>
			<path d="M12 16V4M8 8l4-4 4 4" />
			<path d="M4 16v2a2 2 0 002 2h12a2 2 0 002-2v-2" />
		</Svg>
	);
}

export function DownloadIcon(props: IconProps) {
	return (
		<Svg {...props}>
			<path d="M12 4v12M8 12l4 4 4-4" />
			<path d="M4 16v2a2 2 0 002 2h12a2 2 0 002-2v-2" />
		</Svg>
	);
}

export function ChevronIcon(props: IconProps) {
	return (
		<Svg {...props}>
			<path d="M9 5l7 7-7 7" />
		</Svg>
	);
}

export function CloseIcon(props: IconProps) {
	return (
		<Svg {...props}>
			<path d="M6 6l12 12M18 6L6 18" />
		</Svg>
	);
}

export function CheckIcon(props: IconProps) {
	return (
		<Svg {...props}>
			<path d="M4 12l6 6L20 6" />
		</Svg>
	);
}
